import api from '../api';

/** Mirrors backend/src/routes/promotions.ts BANNER_DESIGNS and components/Promotions/banners' BannerDesign. */
export type BannerDesign =
  | 'services-list' | 'photo-wide' | 'three-up' | 'strip'
  | 'bundle' | 'coupon' | 'ladder' | 'due-next';

/**
 * Promotions admin API.
 *
 * Routes through the shared `api` axios instance (baseURL = REACT_APP_API_URL +
 * '/admin'), so the 401-refresh interceptor applies and paths below are passed
 * WITHOUT the `/admin` prefix — `/promotions` resolves to `/api/admin/promotions`.
 *
 * Mirrors backend/src/routes/promotions.ts: list/insert are owner-or-advisor,
 * create/update/margin are owner-only (enforced server-side — this file adds no
 * client-side gate of its own, same as promoCodesApi.ts).
 */

export type DiscountType = 'percentage' | 'amount' | 'fixed_price';

export interface Promotion {
  id: number;
  shop_id: number;
  template_id: number | null;
  name: string;
  headline: string | null;
  body: string | null;
  fine_print: string | null;
  banner_design: BannerDesign;
  discount_type: DiscountType | null;
  discount_value: number | null;
  promo_price_cents: number | null;
  list_price_cents: number | null;
  image_attachment_id: number | null;
  image_url: string | null;
  promo_code_id: number | null;
  starts_at: string | null;
  ends_at: string | null;
  is_active: number;
  show_on_receipts: number;
  show_on_portal: number;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
}

/** Everything on a promotion the owner can set. `name` is the one field the server requires. */
export type PromotionInput = Partial<Omit<Promotion, 'id' | 'shop_id' | 'created_by' | 'created_at' | 'updated_at'>> & {
  name: string;
};

/** What the shop keeps on a promotion — owner-only (see GET /:id/margin). */
export interface PromotionMargin {
  promotion_id: number;
  costCents: number;
  profitCents: number;
  percent: number;
  /**
   * False when the figure above understates cost. A promotion built with no
   * costed parts (no template, or a template whose parts carry no cost_cents)
   * always reports true here — see marginForPromotion in backend/src/models/Promotion.ts
   * for exactly which labor-cost path can never resolve for a promotion today.
   * The UI must show this honestly rather than let the owner read a
   * parts-only figure as the whole picture.
   */
  laborCostIncluded: boolean;
}

/** GET /api/admin/promotions — owner or advisor. */
export async function listPromotions(): Promise<Promotion[]> {
  const { data } = await api.get<Promotion[]>('/promotions');
  return data;
}

/** POST /api/admin/promotions — owner-only. */
export async function createPromotion(input: PromotionInput): Promise<Promotion> {
  const { data } = await api.post<Promotion>('/promotions', input);
  return data;
}

/** PUT /api/admin/promotions/:id — owner-only. */
export async function updatePromotion(id: number, patch: Partial<PromotionInput>): Promise<Promotion> {
  const { data } = await api.put<Promotion>(`/promotions/${id}`, patch);
  return data;
}

/** GET /api/admin/promotions/:id/margin — owner-only. Never computed client-side; the server owns the arithmetic. */
export async function getPromotionMargin(id: number): Promise<PromotionMargin> {
  const { data } = await api.get<PromotionMargin>(`/promotions/${id}/margin`);
  return data;
}

export interface InsertPromotionTarget {
  entityType: 'estimate' | 'work_order';
  entityId: number;
}

export interface InsertPromotionResult {
  promotion_id: number;
  target_type: string;
  target_id: number;
  lines_inserted: number;
  target: unknown;
}

/** POST /api/admin/promotions/:id/insert — owner or advisor. */
export async function insertPromotionIntoDocument(
  id: number,
  target: InsertPromotionTarget,
): Promise<InsertPromotionResult> {
  const { data } = await api.post<InsertPromotionResult>(`/promotions/${id}/insert`, {
    target_type: target.entityType,
    target_id: target.entityId,
  });
  return data;
}

/**
 * The live price preview (previewPriceFromDiscount) lives in
 * ../../utils/promotionPricing.ts, NOT here — PromotionBuilder's tests
 * `vi.mock('.../promotionsApi')` wholesale, which would turn this pure
 * function into a mock too if it lived in this file. Re-exported as a value
 * only (this file's own DiscountType/DiscountInput above are structurally
 * identical to that module's, so no separate type re-export is needed).
 */
export { previewPriceFromDiscount } from '../../utils/promotionPricing';

// ─── The backing template ───────────────────────────────────────────────────
//
// Not part of the promotions route — these go through the existing, reviewed
// `/admin/estimate-templates` endpoint (backend/src/routes/estimate-templates.ts),
// the same one EstimateTemplateForm posts to. A promotion prices from a
// `template_id` (models/Promotion.ts: resolvePromotionLines); without one it
// bills as a single line with no parts, so marginForPromotion has nothing but
// $0 cost to work with. Migration 215 added `template_parts.cost_cents` /
// `markup_percentage` / `markup_preset_id` specifically so this screen could
// give margin a real basis (see that migration's own doc comment); the
// matching write path was missing from `EstimateTemplateModel.createPart`
// until this task added it.

export interface PromotionTemplatePart {
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  discounted_price?: number | null;
  availability: 'in_stock' | 'order_required' | 'backordered' | 'discontinued';
  supplier?: string | null;
  notes?: string | null;
  cost_cents?: number | null;
  markup_percentage?: number | null;
  markup_preset_id?: number | null;
}

export interface PromotionTemplateLine {
  category: string;
  description: string;
  quantity: number;
  labor_hours?: number | null;
  hourly_rate?: number | null;
  labor_discount_percentage?: number | null;
  labor_discount_amount?: number | null;
  total_price: number;
  notes?: string | null;
  parts?: PromotionTemplatePart[];
}

export interface SavePromotionTemplateInput {
  template_name: string;
  category: string;
  is_public: boolean;
  is_active: boolean;
  is_general: boolean;
  estimated_duration_hours: number;
  line_items: PromotionTemplateLine[];
}

/** POST /api/admin/estimate-templates. */
export async function createPromotionTemplate(input: SavePromotionTemplateInput): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>('/estimate-templates', input);
  return data;
}

/** PUT /api/admin/estimate-templates/:id. */
export async function updatePromotionTemplate(id: number, input: SavePromotionTemplateInput): Promise<{ id: number }> {
  const { data } = await api.put<{ id: number }>(`/estimate-templates/${id}`, input);
  return data;
}

export interface FetchedTemplateLinePart {
  part_number: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  discounted_price?: number | null;
  availability: 'in_stock' | 'order_required' | 'backordered' | 'discontinued';
  supplier?: string | null;
  notes?: string | null;
  cost_cents?: number | null;
  markup_percentage?: number | null;
  markup_preset_id?: number | null;
}

export interface FetchedTemplateLine extends PromotionTemplateLine {
  id?: number;
  parts?: FetchedTemplateLinePart[];
}

export interface FetchedTemplate {
  id: number;
  template_name: string;
  line_items: FetchedTemplateLine[];
}

/** GET /api/admin/estimate-templates/:id — used to reopen an existing promotion's job lines for editing. */
export async function getPromotionTemplate(id: number): Promise<FetchedTemplate> {
  const { data } = await api.get<FetchedTemplate>(`/estimate-templates/${id}`);
  return data;
}

/** POST /api/admin/upload/template-media — a promotion's photo. Same upload endpoint EstimateTemplateForm uses for its own image. */
export async function uploadPromotionPhoto(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<{ url: string }>('/upload/template-media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
}
